import React, { useState } from 'react';
import {
  Box,
  Heading,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Spacer,
  Flex,
  CircularProgress,
  Tooltip,
  IconButton,
  useDisclosure,
} from '@chakra-ui/react';
import { MdHistory } from 'react-icons/md';
import { Paper } from '../../components/atoms/Paper';
import { InterviewResultHistory } from '../../types/interviewResultHistory';
import { dateConvert } from '../../utils/dateConvert';
import { useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useGetMetadata } from '../../hooks/useGetMetadata';
import { GetInterviewResultHistory } from '../../types/interviewResultConfirmApiTypes';
import { toInterviewResultHistory } from '../../domains/toInterviewResultConfirm';
import { getInterviewResultHistories } from '../../api/index';
import { getInterviewResultAnswerHistoryDetail } from '../../api/index';
import { GetAnswerHistoryDetail } from '../../types/answerHistoryApiTypes';
import { Answer } from '../../types/answer';
import { InterviewSheetHistory } from '../../types/interviewSheetHistory';
import {
  toAnswers,
  toInterviewSheetHistory,
} from '../../domains/toInterviewResultConfirm';
import { InterviewResultHistoryDetail } from '../../pages/InterviewResult/InterviewResultHistoryDetail';

type Props = {};

export const UpdateHistory: React.VFC<Props> = () => {
  const { id } = useParams<{ id: string }>();
  const { user, getAccessTokenSilently } = useAuth0();
  const { auth0UserData } = useGetMetadata();
  const { tenantId } = auth0UserData;
  const userId = user?.sub ?? '';
  const userName = encodeURI(user?.name ?? '');
  const [isEmpty, setIsEmpty] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessed, setIsProcess] = useState(false);
  const [histories, setHistories] = useState({} as InterviewResultHistory[]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [answers, setAnswers] = useState({} as Answer[]);
  const [interviewSheetHistory, setInterviewSheetHistory] = useState(
    {} as InterviewSheetHistory,
  );
  const [createdAt, setCreatedAt] = useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleGetHistories = async () => {
    if (isEmpty && !isProcessed) {
      try {
        setIsLoading(true);
        const accessToken = await getAccessTokenSilently({
          audience: 'https://www.webinterview.mic.jp/',
          scope: 'read:interview-results',
        });
        const result = await getInterviewResultHistories(
          tenantId,
          id,
          userId,
          userName,
          accessToken,
        );

        if (result.ok) {
          const data: GetInterviewResultHistory = await result.json();
          if (data.length > 0) {
            setHistories(
              toInterviewResultHistory(data)
                .slice()
                .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
                .filter((history) => history.eventName !== 'change_pnumber'),
            );
            setIsEmpty(false);
          }
        } else {
          console.log(result.status);
        }
        setIsProcess(true);
      } catch (error) {
        console.error({ error });
      } finally {
        setIsLoading(false);
      }
    }
  };
  const updateHistoryConversion = (
    eventName: string,
    previousState: string,
    currentState: string,
    createdByName: string,
  ) => {
    if (eventName === 'change_identifieddate') {
      return `${createdByName} が 本人確認 を ${
        currentState ? '確定' : '解除'
      } しました`;
    } else if (eventName === 'delete_interviewresult') {
      return `${createdByName} が 問診票 を 無効にしました`;
    } else if (eventName === 'change_answer') {
      return `${createdByName} が 質問と回答 を変更しました`;
    } else {
      const display = (state: string) => {
        if (eventName === 'change_sex') {
          return state === 'male' ? '男' : state === 'female' ? '女' : '不明';
        }
        return state;
      };
      const name = () => {
        switch (eventName) {
          case 'change_pid':
            return '患者番号';
          case 'change_lastname':
            return '氏名（姓）';
          case 'change_firstname':
            return '氏名（名）';
          case 'change_lastkana':
            return '氏名カナ（姓）';
          case 'change_firstkana':
            return '氏名カナ（名）';
          case 'change_sex':
            return '性別';
          case 'change_birthday':
            return '生年月日';
          case 'change_zipcode':
            return '郵便番号';
          case 'change_streetaddress':
            return '住所';
          case 'change_phonenumber':
            return '電話番号';
          case 'change_mailaddress':
            return 'メールアドレス';
          case 'change_reservationdate':
            return '予約日時';
          default:
            return '';
        }
      };
      return `${createdByName} が ${name()} を ${display(
        previousState,
      )} から ${display(currentState)} に変更しました`;
    }
  };

  const hadleGetAnswerHistoyDetail = (answerHistoryId: string) => {
    const getData = async (): Promise<void> => {
      try {
        setDetailLoading(true);
        const accessToken = await getAccessTokenSilently({
          audience: 'https://www.webinterview.mic.jp/',
          scope: 'read:interview-results',
        });
        const result = await getInterviewResultAnswerHistoryDetail(
          tenantId,
          id,
          answerHistoryId,
          userId,
          userName,
          accessToken,
        );
        if (result.ok) {
          const data: GetAnswerHistoryDetail = await result.json();
          data.answers ? setAnswers(toAnswers(data.answers)) : ({} as Answer[]);
          data.interview_sheet_history
            ? setInterviewSheetHistory(
                toInterviewSheetHistory({
                  ...data.interview_sheet_history,
                  questions: data.interview_sheet_history.questions
                    .map((question) => ({
                      ...question,
                      question_items: question.question_items
                        .slice()
                        .sort((a, b) => a.sort_number - b.sort_number),
                    }))
                    .slice()
                    .sort((a, b) => a.sort_number - b.sort_number),
                }),
              )
            : ({} as InterviewSheetHistory);
          setCreatedAt(data.created_at);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setDetailLoading(false);
      }
    };
    getData();
  };

  return (
    <Box mx="4">
      <Paper px="5">
        <Accordion allowMultiple onChange={handleGetHistories}>
          <AccordionItem border="none">
            <AccordionButton pt="5" pb="4" px="0" _hover={{ bg: 'white' }}>
              <Heading size="md" color="gray.600" textAlign="left">
                変更履歴
              </Heading>
              <Spacer />
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pt="0" pb="2" px="0">
              {isLoading ? (
                <Flex w="full" justifyContent="center" alignItems="center">
                  <CircularProgress isIndeterminate color="blue.300" />
                </Flex>
              ) : isEmpty ? (
                <Box w="full" justify="center" align="center" mt="3" mb="5">
                  <Text size="sm" color="gray.600" textAlign="left">
                    履歴がありません
                  </Text>
                </Box>
              ) : (
                <>
                  {histories.map(
                    (
                      {
                        eventName,
                        previousState,
                        currentState,
                        createdAt,
                        createdByName,
                      },
                      idx,
                    ) => (
                      <Box
                        key={idx}
                        w="full"
                        py="4"
                        textAlign="left"
                        borderTopWidth="1px"
                      >
                        <Text color="gray.600" fontWeight="bold" fontSize="sm">
                          {dateConvert(createdAt, 'seconds')}
                        </Text>
                        <Flex>
                          <Text color="gray.600" fontWeight="bold" mt="3px">
                            {updateHistoryConversion(
                              eventName,
                              previousState,
                              currentState,
                              createdByName,
                            )}
                          </Text>
                          {eventName === 'change_answer' ? (
                            <Tooltip label="履歴詳細">
                              <IconButton
                                aria-label="edit"
                                icon={<MdHistory />}
                                size="sm"
                                fontSize="xl"
                                borderRadius="full"
                                borderWidth="1px"
                                bg="white"
                                ml="20px"
                                onClick={() => {
                                  hadleGetAnswerHistoyDetail(previousState);
                                  onOpen();
                                }}
                              ></IconButton>
                            </Tooltip>
                          ) : (
                            <></>
                          )}
                        </Flex>
                      </Box>
                    ),
                  )}
                </>
              )}
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </Paper>
      <InterviewResultHistoryDetail
        isOpen={isOpen}
        onClose={onClose}
        answers={answers}
        questions={interviewSheetHistory.questions}
        createdAt={createdAt}
        loading={detailLoading}
      />
    </Box>
  );
};
